import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import toast from "../../utils/toast.jsx";
import { unparse } from "papaparse";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Collapse,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Stack,
  useMediaQuery,
  useTheme,
  Menu,
  MenuItem,
} from "@mui/material";
import {
  Delete,
  Download,
  Backup,
  MoreVert,
  Edit,
} from "@mui/icons-material";
import { EmailsApi } from "../../services/api/emails";
import TierDropdown from "../parts/tierDropdown";
import EmailListImport from "./emailListImport";
import HelpHint from "../parts/helpHint";

function EmailList(props) {
  const [emailList, setEmailList] = useState([]);
  const [email, setEmail] = useState("");
  const [tier, setTier] = useState(0);
  const [note, setNote] = useState("");
  const [tiers, setTiers] = useState([]);
  const [iTiers, setITiers] = useState([]);
  const [isAdding, setIsAdding] = useState(false);
  const [isUploading, setIsUploading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [simpleTiers, setSimpleTiers] = useState([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [filter, setFilter] = useState("");
  const [csvList, setCsvList] = useState([]);
  const [tourTime, setTourTime] = useState(
    localStorage.getItem("tour") === "true"
  );
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editingField, setEditingField] = useState(null);
  const [editingValue, setEditingValue] = useState("");
  const [editingItem, setEditingItem] = useState(null); // Store the full item being edited
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedEmailForMenu, setSelectedEmailForMenu] = useState(null);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [sortField, setSortField] = useState('email');
  const [sortDirection, setSortDirection] = useState('asc');
  
  const isMounted = useRef(true);
  const emailInputRef = useRef(null);
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  useEffect(() => {
    isMounted.current = true;
    window.scrollTo(0, 0);
    getEmailListEndpoint();
    return () => {
      isMounted.current = false;
    };
  }, []);

  const addEmailEndpoint = async () => {
    if (!email) {
      toast.warn("Please enter an email address.");
      return;
    }
    try {
      let token = props.token;
      if (typeof token === 'function') token = await token();
      const response = await EmailsApi.addEmail(token, { email, tier, note });
      
      if (response.success) {
        toast.success("Email added");
        setEmail("");
        setTier(0);
        setNote("");
        getEmailListEndpoint();
      } else {
        toast.error(`Failed to add email: ${response.message || response.status}`);
      }
    } catch (err) {
      toast.error("Error adding email: " + err.message);
    } finally {
      setIsAdding(false);
    }
  };

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = emailList.map((n) => n.id);
      setSelectedEmails(newSelecteds);
      setSelectAll(true);
      return;
    }
    setSelectedEmails([]);
    setSelectAll(false);
  };

  const handleClick = (event, id) => {
    const selectedIndex = selectedEmails.indexOf(id);
    let newSelected = [];

    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selectedEmails, id);
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selectedEmails.slice(1));
    } else if (selectedIndex === selectedEmails.length - 1) {
      newSelected = newSelected.concat(selectedEmails.slice(0, -1));
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selectedEmails.slice(0, selectedIndex),
        selectedEmails.slice(selectedIndex + 1)
      );
    }
    setSelectedEmails(newSelected);
  };

  const isSelected = (id) => selectedEmails.indexOf(id) !== -1;

  const handleMenuClick = (event, email) => {
    setAnchorEl(event.currentTarget);
    setSelectedEmailForMenu(email);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedEmailForMenu(null);
  };

  const handleSortRequest = (field) => {
    const isAsc = sortField === field && sortDirection === 'asc';
    setSortDirection(isAsc ? 'desc' : 'asc');
    setSortField(field);
  };

  const sortData = (data, field, direction) => {
    return [...data].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];
      
      // Handle null/undefined values
      if (aValue == null) aValue = '';
      if (bValue == null) bValue = '';
      
      // Convert to string for comparison
      aValue = String(aValue).toLowerCase();
      bValue = String(bValue).toLowerCase();
      
      if (direction === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  };

  const startEditing = (id, field, currentValue, item = null) => {
    setEditingId(id);
    setEditingField(field);
    setEditingValue(currentValue);
    setEditingItem(item);
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditingField(null);
    setEditingValue("");
    setEditingItem(null);
  };

  const saveEdit = async () => {
    let success = false;
    
    switch (editingField) {
      case 'email':
        success = await submitEditEmail(editingId, editingValue);
        break;
      case 'note':
        success = await submitEditNote(editingId, editingValue);
        break;
      case 'tier':
        success = await submitEditTier(editingId, editingValue);
        break;
      default:
        break;
    }
    
    if (success) {
      cancelEditing();
    }
  };

  const getEmailListEndpoint = async () => {
    try {
      let token = props.token;
      if (typeof token === 'function') {
        token = await token();
      }
      if (!token || typeof token !== 'string' || token === 'undefined') {
        toast.error('No valid token found for email list API call.');
        return;
      }

      const response = await EmailsApi.getEmails(token);

      if (isMounted.current && response.success) {
        const responseJson = response.data;
        let list = [];
        let simpleList = [];

        responseJson.tierlist.forEach((item) => {
          let list_item = {
            title: item.relId,
            relId: item.relId,
            nickname: item.tierName,
            color: item.color,
            parentTier: item.parentTier,
          };

          list.push(list_item);
          simpleList.push(item.tierName);
        });

        let ztier = {};

        list.forEach((list) => {
          let id = list.relId;
          ztier[id] = list;
        });

        setTiers(list);
        setITiers(ztier);
        setSimpleTiers(simpleList);

        localStorage.setItem("emailListTiers", JSON.stringify(list));
        localStorage.setItem("emailListiTiers", JSON.stringify(ztier));
        localStorage.setItem(
          "emailListSimpleTiers",
          JSON.stringify(simpleList)
        );

        let emailListArr = [];
        let csvListArr = [["email", "tier", "note"]];

        responseJson.emaillist.forEach((item) => {
          let tier = parseInt(item.tier);

          let tier_rel_id = tier;
          let tcolor = "#FFFFFF";
          let tn = "No Tier";
          if (
            ztier &&
            ztier[tier] &&
            typeof ztier[tier].relId !== "undefined"
          ) {
            tier_rel_id = ztier[tier].relId;
            tcolor = ztier[tier].color;
            tn = ztier[tier].nickname;
          }

          let list_item = {
            id: item.id,
            note: item.note,
            tier: tn,
            tier_rel_id: tier_rel_id,
            email: item.email,
            color: tcolor,
          };

          emailListArr.push(list_item);
          csvListArr.push([item.email, tn, item.note]);
        });

        localStorage.setItem("emailList", JSON.stringify(emailListArr));
        localStorage.setItem("emailListCsvList", JSON.stringify(csvListArr));

        setEmailList(emailListArr);
        setIsUploading(false);
        setCsvList(csvListArr);
      } else {
        console.error("Component is not mounted or response failed:", response.message);
        toast.error("Failed to load email list: " + (response.message || 'Unknown error'));
      }
    } catch (err) {
      console.error("Error fetching email list endpoint:", err);
      toast.error("Error fetching email list: " + err.message);
    }
  };

  const deleteAllEmails = async () => {
    setConfirmDeleteOpen(true);
  };

  const confirmDeleteAllEmails = async () => {
    setConfirmDeleteOpen(false);
    let token = props.token;
    if (typeof token === 'function') {
      token = await token();
    }
    if (!token || typeof token !== 'string' || token === 'undefined') {
      toast.error('No valid token found for email list API call.');
      return;
    }

    setIsDeleting(true);

    try {
      const response = await EmailsApi.deleteAllEmails(token);

      if (response.success) {
        console.log("successful deletion");
        toast.success("Successful Deletion");
        if (isMounted.current)
          setEmailList([]);
      } else {
        if (isMounted.current) setIsDeleting(false);
        console.log("failure on endpoint", response.status);
        toast.warn("Uh oh, something went wrong: Error: " + (response.status || 'Unknown error'));
      }
    } catch (err) {
      if (isMounted.current) setIsDeleting(false);
      console.log("Error on deletion", err);
      toast.warn("Uh oh, something went wrong: Error: " + err.message);
    }
  };

  const emailDeleteEndpointV2 = async (idsToDelete = null) => {
    try {
      const selectedEmailIds = Array.isArray(idsToDelete) && idsToDelete.length > 0
        ? idsToDelete
        : selectedEmails;

      if (!selectedEmailIds.length) {
        return;
      }

      let emailListCopy = [...emailList];

      setIsDeleting(true);

      for (let i = 0; i < selectedEmailIds.length; i++) {
        const id = selectedEmailIds[i];

        const token = typeof props.token === 'string' ? props.token : (typeof props.token === 'function' ? await props.token() : '');

        const response = await EmailsApi.deleteEmail(token, id);

        if (response.success) {
          console.log("successful deletion");
          emailListCopy = emailListCopy.filter((item) => item.id !== id);
          setEmailList(emailListCopy);
        } else {
          console.log("failure on endpoint", response.status);
          toast.warn("Failed to delete email: " + (response.message || 'Unknown error'));
        }
      }

      setIsDeleting(false);
      setSelectedEmails((prev) => prev.filter((id) => !selectedEmailIds.includes(id)));
    } catch (err) {
      console.log("Error on deletion", err);
      toast.error("Error deleting emails: " + err.message);
      setIsDeleting(false);
    }
  };

  const submitEditNote = async (id, notes) => {
    try {
      const token = typeof props.token === 'string' ? props.token : (typeof props.token === 'function' ? await props.token() : '');

      const response = await EmailsApi.updateEmail(token, id, 'note', notes);

      if (response.success) {
        console.log("successful update");
        toast.success("Note updated successfully");
        // Update the local state immediately for better UX
        setEmailList(prevList => 
          prevList.map(item => 
            item.id === id ? { ...item, note: notes } : item
          )
        );
        return true;
      } else {
        console.log("failure on endpoint", response.status);
        toast.warn("Failed to update note: " + (response.message || 'Unknown error'));
        return false;
      }
    } catch (err) {
      console.log("Error on connection", err);
      toast.error("Error updating note: " + err.message);
      return false;
    }
  };

  const submitEditEmail = async (id, email) => {
    try {
      const token = typeof props.token === 'string' ? props.token : (typeof props.token === 'function' ? await props.token() : '');

      const response = await EmailsApi.updateEmail(token, id, 'email', email);

      if (response.success) {
        console.log("successful update");
        toast.success("Email updated successfully");
        // Update the local state immediately for better UX
        setEmailList(prevList => 
          prevList.map(item => 
            item.id === id ? { ...item, email: email } : item
          )
        );
        return true;
      } else {
        console.log("failure on endpoint", response.status);
        toast.warn("Failed to update email: " + (response.message || 'Unknown error'));
        return false;
      }
    } catch (err) {
      console.log("Error on connection", err);
      toast.error("Error updating email: " + err.message);
      return false;
    }
  };

  const submitEditTier = async (id, tier) => {
    try {
      const token = typeof props.token === 'string' ? props.token : (typeof props.token === 'function' ? await props.token() : '');

      const response = await EmailsApi.updateEmail(token, id, 'tier', tier);

      if (response.success) {
        console.log("successful update");
        toast.success("Tier updated successfully");
        // For tier updates, we need to refresh the entire list to get updated tier display data
        // because we need to resolve tier names and colors
        getEmailListEndpoint();
        return true;
      } else {
        console.log("failure on endpoint", response.status);
        toast.warn("Failed to update tier: " + (response.message || 'Unknown error'));
        return false;
      }
    } catch (err) {
      console.log("Error on connection", err);
      toast.error("Error updating tier: " + err.message);
      return false;
    }
  };

  const filteredEmailList = sortData(
    emailList.filter((item) =>
      item.email.toLowerCase().includes(filter.toLowerCase())
    ),
    sortField,
    sortDirection
  );

  return (
    <Box sx={{ 
      px: isMobile ? 1 : 2,
      py: 1,
      pb: { xs: 10, sm: 6, md: 4 }, // Bottom padding consistent with collections
      maxWidth: '100%',
      overflow: 'hidden'
    }}>
      <HelpHint
        title="Email list"
        hints={[
          "Add subscribers one at a time or import a CSV",
          "Tag emails with a tier for targeted distributions",
          "Use the search and sort controls to find subscribers",
        ]}
      />
      {/* Form to add a single email */}
      {isUploading ? (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 100 }}>
          <CircularProgress />
          <Typography sx={{ ml: 2 }}>Loading tier data...</Typography>
        </Paper>
      ) : (
        <Paper sx={{ p: 2, mb: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={4}>
              <TextField
                label="Email"
                variant="outlined"
                fullWidth
                inputProps={{ 'data-testid': 'email-input' }}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TierDropdown
                token={props.token}
                action={(_, id) => setTier(id)}
                defaultValue={tier}
                size="sm"
                tierData={tiers}
              />
            </Grid>
            <Grid item xs={6} md={3}>
              <TextField
                label="Note (optional)"
                variant="outlined"
                fullWidth
                value={note}
                onChange={(e) => setNote(e.target.value)}
                placeholder="Add a note for this email"
                size={isMobile ? "small" : "medium"}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                variant="contained"
                data-testid="add-email-button"
                onClick={addEmailEndpoint}
                disabled={isAdding}
                startIcon={isAdding ? <CircularProgress size={20} /> : null}
                fullWidth
                size={isMobile ? "small" : "medium"}
              >
                {isAdding ? "Adding..." : "Add Email"}
              </Button>
            </Grid>
          </Grid>
        </Paper>
      )}

      {/* Import / Filter section */}
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box sx={{ 
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 1,
          mb: 2
        }}>
          <Button
            variant="contained"
            startIcon={<Backup />}
            onClick={() => setShowModal(true)}
            size={isMobile ? "small" : "medium"}
          >
            Import
          </Button>
          <Button
            variant="outlined"
            startIcon={<Download />}
            onClick={() => {
              const csv = unparse(csvList);
              const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
              const link = document.createElement("a");
              link.href = URL.createObjectURL(blob);
              link.setAttribute("download", "emails.csv");
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
            size={isMobile ? "small" : "medium"}
          >
            Export
          </Button>
          <Button
            variant="contained"
            color="error"
            startIcon={<Delete />}
            onClick={deleteAllEmails}
            disabled={isDeleting}
            size={isMobile ? "small" : "medium"}
          >
            {isDeleting ? <CircularProgress size={24} /> : "Delete All"}
          </Button>
        </Box>
        <TextField
          label="Filter by email"
          variant="outlined"
          fullWidth
          margin="normal"
          onChange={(e) => setFilter(e.target.value)}
          size={isMobile ? "small" : "medium"}
        />
      </Paper>

      <Dialog open={showModal} onClose={() => setShowModal(false)}>
        <DialogTitle>Import Emails</DialogTitle>
        <DialogContent>
          <EmailListImport
            token={props.token}
            tiers={simpleTiers}
            iTiers={iTiers}
            onClose={() => {
              setShowModal(false);
              getEmailListEndpoint();
            }}
          />
        </DialogContent>
      </Dialog>

      <Collapse in={selectedEmails.length > 0 && !isMobile}>
        <Paper sx={{ p: 2, mb: 2, display: "flex", alignItems: "center" }}>
          <Typography sx={{ flexGrow: 1 }}>
            {selectedEmails.length} selected
          </Typography>
          <IconButton onClick={emailDeleteEndpointV2} disabled={isDeleting}>
            {isDeleting ? <CircularProgress size={24} /> : <Delete />}
          </IconButton>
        </Paper>
      </Collapse>

      {isUploading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress />
        </Box>
      ) : isMobile ? (
        // Mobile Card Layout
        <Box>
          {selectedEmails.length > 0 && (
            <Paper sx={{ p: 2, mb: 2, display: "flex", alignItems: "center" }}>
              <Typography sx={{ flexGrow: 1 }}>
                {selectedEmails.length} selected
              </Typography>
              <IconButton onClick={emailDeleteEndpointV2} disabled={isDeleting}>
                {isDeleting ? <CircularProgress size={24} /> : <Delete />}
              </IconButton>
            </Paper>
          )}
          
          <Stack spacing={2}>
            {filteredEmailList.map((item) => {
              const isItemSelected = isSelected(item.id);
              const isEditingNote = editingId === item.id && editingField === 'note';
              const isEditingEmail = editingId === item.id && editingField === 'email';
              const isEditingTier = editingId === item.id && editingField === 'tier';
              
              return (
                <Card key={item.id} variant="outlined">
                  <CardContent sx={{ pb: 1 }}>
                    {/* Top row: Checkbox, Email (left), Tier (right), Menu */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                      <Checkbox
                        checked={isItemSelected}
                        onChange={(event) => handleClick(event, item.id)}
                        sx={{ p: 0, mr: 1, alignSelf: 'flex-start' }}
                      />
                      {/* Email section - takes up available space on the left */}
                      <Box sx={{ flexGrow: 1, minWidth: 0, mr: 2 }}>
                        {isEditingEmail ? (
                          <TextField
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onBlur={(e) => {
                              if (e.target.value !== (editingItem?.email || '')) {
                                saveEdit();
                              } else {
                                cancelEditing();
                              }
                            }}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                saveEdit();
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelEditing();
                              }
                            }}
                            size="small"
                            fullWidth
                            autoFocus
                          />
                        ) : (
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 'medium',
                              wordBreak: 'break-all',
                              fontSize: '0.875rem',
                              lineHeight: 1.3
                            }}
                          >
                            {item.email}
                          </Typography>
                        )}
                      </Box>
                      {/* Tier section - aligned to the right */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {isEditingTier ? (
                          <TierDropdown
                            token={props.token}
                            action={async (_, newId) => {
                              const success = await submitEditTier(item.id, newId);
                              if (success) {
                                cancelEditing();
                              }
                            }}
                            defaultValue={editingValue}
                            size="small"
                            tierData={tiers}
                          />
                        ) : (
                          <Chip
                            label={item.tier}
                            size="small"
                            clickable
                            onClick={(e) => {
                              e.stopPropagation();
                              startEditing(item.id, 'tier', item.tier_rel_id, item);
                            }}
                            sx={{
                              backgroundColor: item.color,
                              color: theme.palette.getContrastText(item.color),
                              fontSize: '0.75rem',
                              height: '24px'
                            }}
                          />
                        )}
                        <IconButton
                          size="small"
                          onClick={(e) => handleMenuClick(e, item)}
                        >
                          <MoreVert fontSize="small" />
                        </IconButton>
                      </Box>
                    </Box>
                    {/* Note section - always show text field when editing, even if note is empty */}
                    {(item.note || isEditingNote) && (
                      <Box sx={{ mt: 1, ml: 4 }}>
                        {isEditingNote ? (
                          <TextField
                            value={editingValue}
                            onChange={(e) => setEditingValue(e.target.value)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' && !e.shiftKey) {
                                e.preventDefault();
                                if (editingValue !== (editingItem?.note || '')) {
                                  saveEdit();
                                } else {
                                  cancelEditing();
                                }
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                cancelEditing();
                              }
                            }}
                            onBlur={(e) => {
                              if (editingValue !== (editingItem?.note || '')) {
                                saveEdit();
                              } else {
                                cancelEditing();
                              }
                            }}
                            size="small"
                            fullWidth
                            multiline
                            autoFocus
                          />
                        ) : (
                          <Typography
                            variant="body2"
                            sx={{
                              color: theme.palette.text.secondary,
                              fontStyle: 'italic',
                              fontSize: '0.875rem'
                            }}
                          >
                            {item.note || 'Add note...'}
                          </Typography>
                        )}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              );
            })}
          </Stack>
        </Box>
      ) : (
        // Desktop Table Layout
        <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    indeterminate={
                      selectedEmails.length > 0 &&
                      selectedEmails.length < emailList.length
                    }
                    checked={
                      emailList.length > 0 &&
                      selectedEmails.length === emailList.length
                    }
                    onChange={handleSelectAllClick}
                  />
                </TableCell>
                <TableCell sortDirection={sortField === 'email' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'email'}
                    direction={sortField === 'email' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('email')}
                  >
                    Email
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'tier' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'tier'}
                    direction={sortField === 'tier' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('tier')}
                  >
                    Tier
                  </TableSortLabel>
                </TableCell>
                <TableCell sortDirection={sortField === 'note' ? sortDirection : false}>
                  <TableSortLabel
                    active={sortField === 'note'}
                    direction={sortField === 'note' ? sortDirection : 'asc'}
                    onClick={() => handleSortRequest('note')}
                  >
                    Note
                  </TableSortLabel>
                </TableCell>
                <TableCell align="center">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredEmailList.map((item) => {
                const isItemSelected = isSelected(item.id);
                const isEditingTier = editingId === item.id && editingField === 'tier';
                return (
                  <TableRow
                    key={item.id}
                    hover
                    onClick={(event) => handleClick(event, item.id)}
                    role="checkbox"
                    aria-checked={isItemSelected}
                    selected={isItemSelected}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell padding="checkbox">
                      <Checkbox checked={isItemSelected} />
                    </TableCell>
                    <TableCell>
                      {editingId === item.id && editingField === 'email' ? (
                        <TextField
                          inputRef={emailInputRef}
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={(e) => {
                            if (e.target.value !== (editingItem?.email || '')) {
                              saveEdit();
                            } else {
                              cancelEditing();
                            }
                          }}
                          onKeyPress={(e) => e.key === 'Enter' && saveEdit()}
                          size="small"
                          autoFocus
                          fullWidth
                        />
                      ) : (
                        <Typography
                          sx={{ wordBreak: 'break-all', cursor: 'pointer' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(item.id, 'email', item.email, item);
                          }}
                        >
                          {item.email}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {isEditingTier ? (
                        <TierDropdown
                          token={props.token}
                          action={async (_, newId) => {
                            const success = await submitEditTier(item.id, newId);
                            if (success) {
                              cancelEditing();
                            }
                          }}
                          defaultValue={editingValue}
                          size="small"
                          tierData={tiers}
                        />
                      ) : (
                        <Chip
                          label={item.tier}
                          size="small"
                          clickable
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(item.id, 'tier', item.tier_rel_id, item);
                          }}
                          sx={{
                            backgroundColor: item.color,
                            color: theme.palette.getContrastText(item.color)
                          }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      {editingId === item.id && editingField === 'note' ? (
                        <TextField
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          onBlur={(e) => {
                            if (editingValue !== (editingItem?.note || '')) {
                              saveEdit();
                            } else {
                              cancelEditing();
                            }
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' && !e.shiftKey) {
                              e.preventDefault();
                              if (editingValue !== (editingItem?.note || '')) {
                                saveEdit();
                              } else {
                                cancelEditing();
                              }
                            } else if (e.key === 'Escape') {
                              e.preventDefault();
                              cancelEditing();
                            }
                          }}
                          size="small"
                          fullWidth
                          multiline
                          autoFocus
                        />
                      ) : (
                        <Typography
                          sx={{ cursor: 'pointer',
                            color: item.note ? 'inherit' : theme.palette.text.secondary,
                            fontStyle: item.note ? 'normal' : 'italic',
                            fontSize: item.note ? '0.975rem' : '0.825rem',
                            lineHeight: 1.3
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            startEditing(item.id, 'note', item.note || '', item);
                          }}
                        >
                          {item.note || 'Add note...'}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <IconButton
                        size="small"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMenuClick(e, item);
                        }}
                      >
                        <MoreVert />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Context Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        {isMobile ? [
          // Mobile shows all options
          /* Edit Email */
          <MenuItem key="edit-email" onClick={() => {
            handleMenuClose();
            setTimeout(() => {
              startEditing(selectedEmailForMenu?.id, 'email', selectedEmailForMenu?.email, selectedEmailForMenu);
            }, 0);
          }}>
            <Edit fontSize="small" sx={{ mr: 1 }} />
            Edit Email
          </MenuItem>,
          /* Edit Note / Add Note */
          <MenuItem key="edit-note" onClick={() => {
            handleMenuClose();
            setTimeout(() => {
              startEditing(selectedEmailForMenu?.id, 'note', selectedEmailForMenu?.note || '', selectedEmailForMenu);
            }, 0);
          }}>
            <Edit fontSize="small" sx={{ mr: 1 }} />
            {selectedEmailForMenu?.note ? 'Edit Note' : 'Add Note'}
          </MenuItem>,
          /* Delete */
          <MenuItem key="delete" onClick={() => {
            const id = selectedEmailForMenu?.id;
            if (id) {
              emailDeleteEndpointV2([id]);
            }
            handleMenuClose();
          }}>
            <Delete fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        ] : (
          // Desktop shows only delete
          <MenuItem onClick={() => {
            const id = selectedEmailForMenu?.id;
            if (id) {
              emailDeleteEndpointV2([id]);
            }
            handleMenuClose();
          }}>
            <Delete fontSize="small" sx={{ mr: 1 }} />
            Delete
          </MenuItem>
        )}
      </Menu>

      {/* Delete All Confirmation Dialog */}
      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Confirm Delete All</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete all emails? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)}>Cancel</Button>
          <Button onClick={confirmDeleteAllEmails} color="error" variant="contained">
            Delete All
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default EmailList;
